import { Injectable } from '@angular/core';

import { WebApiModel } from '../../models/web-api.model';
import { RouterHelperService, RouteUrl } from '../../services/router-helper.service';
import { SettingsService } from '../../services/settings.service';
import { StateService } from '../../services/state.service';
import { UiService } from '../../services/ui.service';

@Injectable()
export class SearchInputHelperService {
  // Properties and 'type' value need to match the values coming from web api, the first letter is capital.
  get autocompleteItemType() {
    return {
      People: {
        type: 'People',
        cssClass: 'person',
        route: RouterHelperService.routeType.personProfile,
        searchResultsType: null,
      },
      Places: {
        type: 'Places',
        cssClass: 'place',
        route: RouterHelperService.routeType.placeProfile,
        searchResultsType: null,
      },
      HeaderPeople: {
        type: 'HeaderPeople',
        cssClass: 'header-people',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.people,
      },
      HeaderPlaces: {
        type: 'HeaderPlaces',
        cssClass: 'header-places',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.places,
      },
      HeaderServiceCategories: {
        type: 'HeaderServiceCategories',
        cssClass: 'header-service-categories',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.serviceCategories,
      },
      HeaderServiceDescriptions: {
        type: 'HeaderServiceDescriptions',
        cssClass: 'header-services',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.services,
      },
      ServiceCategories: {
        type: 'ServiceCategories',
        cssClass: 'service-category',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.serviceCategory,
      },
      ServiceDescriptions: {
        type: 'ServiceDescriptions',
        cssClass: 'service',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.service,
      },
      HeaderSpecialties: {
        type: 'HeaderSpecialties',
        cssClass: 'header-specialties',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.specialties,
      },
      Specialties: {
        type: 'Specialties',
        cssClass: 'specialty',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: SettingsService.searchResultsType.specialty,
      },
      NoResults: {
        type: 'NoResults',
        cssClass: 'no-results',
        route: RouterHelperService.routeType.searchResults,
        searchResultsType: null,
      },
      Location: {
        // Type for items in the locations dropdown
        type: 'Location',
        cssClass: null,
        route: null,
        searchResultsType: null,
      },
      MyLocation: {
        // Type for the My Location item which will try to get current location
        type: 'MyLocation',
        cssClass: 'my-location',
        route: null,
        searchResultsType: null,
      },
    };
  }

  constructor(
    private _stateService: StateService,
    private _uiService: UiService,
  ) {
    this.bindKeypress();
  }

  bindKeypress() {
    // Bind keypress event to the document, catch the Esc key so if Esc is pressed
    // then we close autocomplete dropdowns even when autocomplete input boxes are not in focus in case if we go
    // with the implementaiton when the dropdown is not closed on blur.
    this._uiService.bindKeyDown(document, (event) => {
      if (event.which === 27) {
        this._stateService.keyPressed(event.which);
      }
    });
  }

  /**
   *  A helper method to get an atuocmplete item route based on the items type, i.e. Person, Place, etc.
   * @param location - Location name
   * @param latitude - My location
   * @param longitude - My location
   */
  getAutocompleteRoute(
    item: WebApiModel.AutocompleteItem,
    defaultCategory: string,
    searchTerm: string,
    location: string,
    latitude: string,
    longitude: string,
    myLocationLabel: string,
  ): RouteUrl {
    const autocompleteItemType = this.getAutocompeteItemType(item.type);
    const routeType = autocompleteItemType.route;
    const searchResultsType = autocompleteItemType.searchResultsType;

    return this.getRoute(
      routeType,
      item.id,
      item.name,
      defaultCategory,
      searchTerm,
      location,
      searchResultsType,
      latitude,
      longitude,
      myLocationLabel,
    );
  }

  getRoute(
    routeType: string,
    id: string,
    name: string,
    defaultCategory: string,
    searchTerm: string,
    location: string,
    searchResultsType: string,
    latitude: string,
    longitude: string,
    myLocationLabel: string,
  ) {
    const urlParams = {};

    const lastDefaultCategory = this._stateService.getLastDefaultCategory();
    const lastSearchType = this._stateService.getLastSearchType();

    if (searchTerm) {
      urlParams[RouterHelperService.urlParam.term] = searchTerm.trim();
    }

    // Use either my location coordinates or location name in the query string.
    if (latitude && longitude) {
      urlParams[RouterHelperService.urlParam.latitude] = latitude;
      urlParams[RouterHelperService.urlParam.longitude] = longitude;
    }
    if (location) {
      urlParams[RouterHelperService.urlParam.location] = location.trim();
    }
    if (defaultCategory) {
      urlParams[RouterHelperService.urlParam.defaultCategory] = defaultCategory;
    }
    if (lastDefaultCategory) {
      urlParams[RouterHelperService.urlParam.defaultCategory] = lastDefaultCategory;
      // this._stateService.clearLastDefaultCategory();
    }
    if (searchResultsType) {
      urlParams[RouterHelperService.urlParam.searchType] = searchResultsType.toLowerCase();
    }
    if (lastSearchType) {
      urlParams[RouterHelperService.urlParam.searchType] = lastSearchType.toLowerCase();
      this._stateService.clearLastSearchType();
    }

    if (myLocationLabel) {
      urlParams[RouterHelperService.urlParam.myLocationLabel] = myLocationLabel;
    }

    return RouterHelperService.getRoute(routeType, id, name, urlParams);
  }

  getAutocompeteItemType(type: string): IAutocompleteItemType {
    return this.autocompleteItemType[type]
      ? this.autocompleteItemType[type]
      : { type: '', cssClass: '', route: '', searchResultsType: '' };
  }

  stripSearchTermPrefix(searchTerm: string): string {
    if (searchTerm) {
      const resultArray = searchTerm.split(':');
      if (resultArray.length > 1) {
        return resultArray[1];
      } else {
        return resultArray[0];
      }
    }
    return '';
  }

  getSearchTermPrefix(searchTerm: string): string {
    if (searchTerm) {
      const resultArray = searchTerm.split(':');
      if (resultArray.length > 1) {
        return resultArray[0];
      }
    }
    return null;
  }
}

interface IAutocompleteItemType {
  type: string;
  cssClass: string;
  route: string;
  searchResultsType: string; // Passed to the search results page as a query string parameter
}
